import React, { useState, useEffect } from "react";
import api from "../api";

const DEFAULT_SUBJECT = "⛳ {{event_name}} – Please Let Us Know You're Coming!";
const DEFAULT_BODY =
`Hi {{first_name}},

Our next event is on {{date}} at {{venue}}.

Tap to let us know you're coming:

{{link_golf_dinner}}
{{link_golf_only}}
{{link_dinner_only}}

{{link_buggy}}

Want to change your answer later? {{vote_link}}

Thanks,
Neil Workman`;

const MERGE_TAGS = ["first_name", "name", "event_name", "date", "venue",
  "link_golf_dinner", "link_golf_only", "link_dinner_only", "link_buggy", "vote_link"];

// Players are stored "Surname Firstname" (e.g. "Brazier David (1)") — the
// first name is always the second word, matching the backend's firstName().
// Keep NICKNAME_OVERRIDES in sync with backend/routes/mailmerge.js.
const NICKNAME_OVERRIDES = { 'Sellman Michael': 'Mike' };
function firstNameOf(fullName) {
  if (NICKNAME_OVERRIDES[fullName]) return NICKNAME_OVERRIDES[fullName];
  const parts = (fullName || '').trim().split(/\s+/);
  return parts.length > 1 ? parts[1] : parts[0];
}

export default function MailMerge() {
  const [event, setEvent] = useState(null);
  const [players, setPlayers] = useState([]);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [selected, setSelected] = useState({});
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/mailmerge/context")
      .then(r => r.data)
      .then(d => {
        setEvent(d.event);
        setPlayers(d.players || []);
        // Default: everyone with an email who hasn't voted yet.
        const init = {};
        (d.players || []).forEach(p => { init[p.id] = !!p.player_email && !p.attendance; });
        setSelected(init);
      })
      .catch(() => {});
  }, []);

  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const selectAll = (val) => {
    const next = {};
    players.forEach(p => { next[p.id] = val && !!p.player_email; });
    setSelected(next);
  };

  const withEmail = players.filter(p => p.player_email);
  const withoutEmail = players.filter(p => !p.player_email);
  const recipientIds = players.filter(p => selected[p.id]).map(p => p.id);

  // Sent as HTML by default (routes/mailmerge.js sends html+text together) —
  // the preview renders real markup here too, not just an approximation, so
  // what you see is what recipients actually get: ✅ for whichever option
  // this player has already picked, ☐ for the rest, each one the clickable
  // link itself rather than a bare URL next to a label.
  const ATTENDANCE_LABEL_HTML = { golf_dinner: "Golf &amp; Dinner", golf_only: "Golf Only", dinner_only: "Dinner Only" };
  const linkHtml = (label) => `<a href="#" style="color:#166534;font-weight:700;text-decoration:none;" onclick="return false;">${label}</a>`;
  const escapeHtml = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const previewPlayer = players.find(p => selected[p.id]) || players[0];
  const previewCtxHtml = {
    name: escapeHtml(previewPlayer ? previewPlayer.name : "Player Name"),
    first_name: escapeHtml(previewPlayer ? firstNameOf(previewPlayer.name) : "Player"),
    event_name: escapeHtml(event ? event.name : ""),
    date: escapeHtml(event && event.date ? new Date(event.date).toLocaleDateString("en-GB") : ""),
    venue: escapeHtml(event ? event.venue : ""),
    vote_link: linkHtml("golfsocietymanager.com/vote"),
  };
  for (const choice of ["golf_dinner", "golf_only", "dinner_only"]) {
    const checked = previewPlayer && previewPlayer.attendance === choice;
    previewCtxHtml["link_" + choice] = linkHtml(`${checked ? "✅" : "☐"} ${ATTENDANCE_LABEL_HTML[choice]}`);
  }
  previewCtxHtml.link_buggy = linkHtml(`${previewPlayer && previewPlayer.buggy ? "✅" : "☐"} Buggy`);

  const mergePreviewHtml = (t) => escapeHtml(t).replace(/\n/g, "<br>")
    .replace(/\{\{(\w+)\}\}/g, (m, k) => (previewCtxHtml[k] !== undefined ? previewCtxHtml[k] : m));

  const send = async () => {
    if (!recipientIds.length) { alert("Select at least one recipient"); return; }
    if (!window.confirm(`Send this email to ${recipientIds.length} member(s)? This cannot be undone.`)) return;
    setSending(true);
    setResult(null);
    try {
      const res = await api.post("/mailmerge/send", { subject, body, playerIds: recipientIds });
      setResult(res.data);
    } catch (e) {
      setResult({ error: "Network error" });
    }
    setSending(false);
  };

  if (!event) {
    return <div style={{ padding: 24, color: "#6b7280" }}>No upcoming event is set up yet.</div>;
  }

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: 20 }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: "#14532d", marginBottom: 4 }}>Mail Merge — Event Reminder</h1>
      <p style={{ color: "#6b7280", fontSize: 14, marginTop: 0 }}>
        {event.name} · {event.date ? new Date(event.date).toLocaleDateString("en-GB") : ""} · {event.venue}
      </p>

      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 420px", minWidth: 320 }}>
          <label style={labelStyle}>Subject</label>
          <input value={subject} onChange={e => setSubject(e.target.value)} style={inputStyle} />

          <label style={labelStyle}>Body</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={12} style={{ ...inputStyle, fontFamily: "inherit" }} />

          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
            Merge tags: {MERGE_TAGS.map(t => <code key={t} style={codeStyle}>{`{{${t}}}`}</code>)}
          </div>

          <div style={{ marginTop: 20, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 8 }}>PREVIEW (as {previewPlayer ? previewPlayer.name : "Player Name"}) — sent as HTML</div>
            <div style={{ fontWeight: 700, marginBottom: 6 }} dangerouslySetInnerHTML={{ __html: mergePreviewHtml(subject) }} />
            <div style={{ fontSize: 14, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: mergePreviewHtml(body) }} />
          </div>
        </div>

        <div style={{ flex: "1 1 280px", minWidth: 260 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <label style={{ ...labelStyle, marginTop: 0 }}>Recipients ({recipientIds.length} selected)</label>
            <div>
              <button onClick={() => selectAll(true)} style={smallBtnStyle}>All</button>
              <button onClick={() => selectAll(false)} style={smallBtnStyle}>None</button>
            </div>
          </div>
          <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 10 }}>
            {withEmail.map(p => (
              <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderBottom: "1px solid #f3f4f6", fontSize: 14 }}>
                <input type="checkbox" checked={!!selected[p.id]} onChange={() => toggle(p.id)} />
                <span style={{ flex: 1 }}>{p.name}</span>
                <span style={{ fontSize: 11, color: p.attendance ? "#166534" : "#9ca3af" }}>{p.attendance ? "voted" : "no response"}</span>
              </label>
            ))}
          </div>

          {withoutEmail.length > 0 && (
            <div style={{ marginTop: 12, background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: 10, fontSize: 12, color: "#854d0e" }}>
              No email on file, will be skipped: {withoutEmail.map(p => p.name).join(", ")}
            </div>
          )}

          <button onClick={send} disabled={sending || !recipientIds.length}
            style={{ marginTop: 16, width: "100%", background: "#14532d", color: "white", border: "none", borderRadius: 8, padding: "12px", fontSize: 15, fontWeight: 700, cursor: sending ? "default" : "pointer", opacity: sending ? 0.6 : 1 }}>
            {sending ? "Sending..." : `Send to ${recipientIds.length} member(s)`}
          </button>

          {result && (
            <div style={{ marginTop: 12, fontSize: 13 }}>
              {result.error && <div style={{ color: "#b91c1c" }}>{result.error}</div>}
              {result.sent && <div style={{ color: "#166534" }}>Sent: {result.sent.length}</div>}
              {result.skipped && result.skipped.length > 0 && <div style={{ color: "#b91c1c" }}>Skipped: {result.skipped.join(", ")}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginTop: 14, marginBottom: 4 };
const inputStyle = { width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: "10px 12px", fontSize: 14, boxSizing: "border-box" };
const codeStyle = { background: "#eef2ff", color: "#3730a3", borderRadius: 4, padding: "1px 5px", marginRight: 6, fontSize: 12 };
const smallBtnStyle = { fontSize: 11, border: "1px solid #d1d5db", background: "white", borderRadius: 6, padding: "3px 8px", marginLeft: 4, cursor: "pointer" };
